import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import {catchError, finalize, from, map, Observable, switchMap, tap, throwError, timer} from 'rxjs';
import {RETRY_CONFIG} from '../utils/retry-context.tokens';
import {inject} from '@angular/core';
import {LoadingService} from '../../shared/components/loading/loading.service';
import {NetworkService} from '../services/network.service';

export const retryInterceptor: HttpInterceptorFn = (req, next) => {
  const loadingService = inject(LoadingService);
  const network = inject(NetworkService);
  const config = req.context.get(RETRY_CONFIG);

  if (!config.enabled) {
    return next(req);
  }

  const maxRetries = config.maxRetries ?? 5;
  const baseDelayMs = config.baseDelayMs ?? 1000;
  const maxDelayMs = config.maxDelayMs ?? 10000;
  const jitterMs = config.jitterMs ?? 500;
  const nodes = config.nodes ?? [];

  let huboRetry = false;
  let retrySuccessShown = false;

  const execute = (
    request: HttpRequest<unknown>,
    attempt: number
  ): Observable<HttpEvent<unknown>> => {
    return next(request).pipe(
      map(event => {
        if (event instanceof HttpResponse) {
          const body: any = event.body;

          if (isRetryableServiceError(body)) {
            throw new HttpErrorResponse({
              error: body,
              status: 250,
              statusText: 'Servicio NO Disponible',
              url: request.url
            });
          }
        }

        return event;
      }),

      tap({
        next: event => {
          if (!huboRetry || retrySuccessShown) return;
          if (event.type !== HttpEventType.Response) return;

          retrySuccessShown = true;
          loadingService.retrySuccess();
          network.setOnline();
          network.closeNetworkModal();
        }
      }),

      catchError((error: HttpErrorResponse) => {
        const shouldRetry =
          error.status === 0 ||
          error.status === 250 ||
          error.status === 408 ||
          error.status === 429 ||
          error.status >= 500;

        if (!shouldRetry || attempt >= maxRetries) {
          return throwError(() => error);
        }

        return from(network.isLocalNetworkPermissionDenied()).pipe(
          switchMap(permissionDenied => {
            if (permissionDenied) {
              network.setLocalNetworkDenied();
              return throwError(() => error);
            }

            huboRetry = true;

            const nextAttempt = attempt + 1;

            const exponentialDelay = Math.min(
              baseDelayMs * Math.pow(2, nextAttempt - 1),
              maxDelayMs
            );

            const jitter = Math.floor(Math.random() * jitterMs);
            const delayMs = exponentialDelay + jitter;

            const nextReq = buildRequestForAttempt(req, nodes, nextAttempt);

            loadingService.retry(
              nextAttempt,
              maxRetries,
              delayMs
            );

            console.warn(
              `Reintento ${nextAttempt}/${maxRetries} en ${delayMs}ms`,
              nextReq.url,
              'status:',
              error.status
            );

            return timer(delayMs).pipe(
              switchMap(() => execute(nextReq, nextAttempt))
            );
          })
        );
      })
    );
  };

  return execute(req, 0).pipe(
    finalize(() => {
      if (huboRetry) {
        setTimeout(() => {
          loadingService.hide();
        }, 1500);
      }
    })
  );
};

function isRetryableServiceError(body: any): boolean {
  const exception = body?.exception ?? body?.body?.exception ?? body?.message;

  return (
    exception?.code === '001' &&
    exception?.module === 'IFR' &&
    exception?.message === 'Servicio NO Disponible'
  );
}
function buildRequestForAttempt(
  req: HttpRequest<unknown>,
  nodes: string[],
  attempt: number
): HttpRequest<unknown> {
  if (!nodes.length || attempt <= 0) return req;

  const nodeIndex = (attempt - 1) % nodes.length;
  const nextUrl = replaceBaseUrl(req.url, nodes[nodeIndex]);

  return req.clone({
    url: nextUrl
  });
}

function replaceBaseUrl(
  originalUrl: string,
  newBaseUrl: string
): string {
  const current = new URL(originalUrl);
  const target = new URL(newBaseUrl);

  current.protocol = target.protocol;
  current.host = target.host;

  return current.toString();
}

/*export const retryInterceptor: HttpInterceptorFn = (req, next) => {
  const loadingService = inject(LoadingService);
  const network = inject(NetworkService);
  const config = req.context.get(RETRY_CONFIG);

  if (!config.enabled) {
    return next(req);
  }

  const maxRetries = config.maxRetries ?? 5;
  const baseDelayMs = config.baseDelayMs ?? 1000;
  const maxDelayMs = config.maxDelayMs ?? 10000;
  const jitterMs = config.jitterMs ?? 500;

  let huboRetry = false;
  let retrySuccessShown = false;

  return next(req).pipe(

    map(event => {
      if (event instanceof HttpResponse) {

        const body: any = event.body;

        const isServiceDown =
          event.status === 250 ||
          body?.status === 250 ||
          body?.exception?.message === 'Servicio NO Disponible' ||
          body?.body?.exception?.message === 'Servicio NO Disponible';

        if (isServiceDown) {
          throw new HttpErrorResponse({
            error: body,
            status: 250,
            statusText: 'Servicio NO Disponible',
            url: req.url
          });
        }
      }

      return event;
    }),

    retry({
      count: maxRetries,
      delay: (error: HttpErrorResponse, retryCount) => {
        console.log('error ==>>', error);
        const shouldRetry =
          error.status === 0 ||
          error.status === 250 ||
          error.status === 408 ||
          error.status === 429 ||
          error.status >= 500;

        if (!shouldRetry) {
          return throwError(() => error);
        }

        huboRetry = true;

        const exponentialDelay = Math.min(
          baseDelayMs * Math.pow(2, retryCount - 1),
          maxDelayMs
        );

        const jitter = Math.floor(Math.random() * jitterMs);
        const delayMs = exponentialDelay + jitter;

        loadingService.retry(retryCount, maxRetries, delayMs);

        console.warn(
          `Reintento ${retryCount}/${maxRetries} en ${delayMs}ms`,
          req.url,
          'status:',
          error.status
        );

        return timer(delayMs);
      }
    }),
    tap({
      next: (event) => {
        if (!huboRetry || retrySuccessShown) return;
        if (event.type !== HttpEventType.Response) return;

        retrySuccessShown = true;
        loadingService.retrySuccess();
        network.setOnline();
        network.closeNetworkModal();
      }
    }),

    finalize(() => {
      if (huboRetry) {
        setTimeout(() => {
          loadingService.hide();
        }, 1500);
      }
    })
  );
};*/
